/**
 * @fileoverview Zustand Store para gerenciamento de versões da DRE
 * @module stores/versionStore
 * 
 * Store para versionamento com:
 * - Salvar snapshots
 * - Histórico de versões
 * - Restauração
 * - Comparação de versões
 */

import { create } from 'zustand';
import toast from 'react-hot-toast';

import type { DREVersion, VersionDiff, PremissasSnapshot } from '../types/version';
import { calculateDiff } from '../lib/versionDiff';
import { supabase } from '../lib/supabase';
import { useDREStore } from './dreStore';

// ============================================================================
// TIPOS
// ============================================================================

interface VersionStore {
  // Estado
  versions: DREVersion[];
  currentVersionId: string | null; // ✅ NOVO: ID da versão atualmente ativa
  baseVersionId: string | null; // ✅ NOVO: ID da versão base (de onde foi restaurado)
  isLoading: boolean;
  isSaving: boolean;
  
  // Actions
  loadVersions: () => Promise<void>;
  saveVersion: (name: string, description?: string, isAutoCheckpoint?: boolean) => Promise<void>;
  restoreVersion: (versionId: string) => Promise<void>;
  deleteVersion: (versionId: string) => Promise<void>;
  compareVersions: (v1Id: string, v2Id: string) => VersionDiff[];
  clearBaseVersion: () => void; // ✅ NOVO: Limpa versão base após salvar explicitamente
  
  // 🎯 SPRINT 3: Associar premissas à versão DRE ativa
  updateVersionWithPremissas: (versionId: string, snapshot: PremissasSnapshot) => void;
}

// ============================================================================
// STORE
// ============================================================================

export const useVersionStore = create<VersionStore>((set, get) => ({
  // Estado inicial
  versions: [],
  currentVersionId: null, // ✅ NOVO: Rastreia versão ativa
  baseVersionId: null, // ✅ NOVO: Rastreia versão base (de onde foi restaurado)
  isLoading: false,
  isSaving: false,

  // ==========================================================================
  // CARREGAMENTO
  // ==========================================================================

  loadVersions: async () => {
    set({ isLoading: true });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        set({ versions: [], isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('dre_versions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const versions: DREVersion[] = (data || []).map((v) => ({
        id: v.id,
        userId: v.user_id,
        versionNumber: v.version_number,
        name: v.name,
        description: v.description || undefined,
        dreData: {
          id: crypto.randomUUID(),
          userId: v.user_id,
          setima: {
            companyId: 'setima' as const,
            companyName: 'Sétima',
            years: v.dre_data_snapshot.setima_years,
          },
          studio555: {
            companyId: '555' as const,
            companyName: '555 Studios',
            years: v.dre_data_snapshot.studio555_years,
          },
          createdAt: new Date(v.created_at),
          updatedAt: new Date(v.created_at),
        },
        diff: v.diff || null,
        premissasSnapshot: v.premissas_snapshot || undefined, // 🎯 SPRINT 3: Carregar premissas
        isAutoCheckpoint: v.is_auto_checkpoint || undefined, // 🎯 SPRINT 4
        createdAt: new Date(v.created_at),
        createdBy: v.created_by,
      }));

      // 🐛 FIX: Inicializar baseVersionId se estiver null e houver versões
      // Isso resolve o warning "baseVersionId: null" ao salvar premissas
      const currentState = get();
      if (!currentState.baseVersionId && versions.length > 0) {
        console.log('[versionStore] 🔧 Inicializando baseVersionId com versão mais recente:', {
          versionId: versions[0].id,
          versionName: versions[0].name,
          versionNumber: versions[0].versionNumber,
        });
        set({ 
          versions,
          baseVersionId: versions[0].id, // Versão mais recente
        });
      } else {
        set({ versions });
      }
    } catch (error) {
      console.error('Erro ao carregar versões:', error);
      toast.error('Erro ao carregar histórico de versões');
    } finally {
      set({ isLoading: false });
    }
  },

  // ==========================================================================
  // SALVAMENTO DE VERSÃO
  // ==========================================================================

  saveVersion: async (name, description, isAutoCheckpoint = false) => {
    set({ isSaving: true });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Você precisa estar logado para salvar versões');
        set({ isSaving: false });
        return;
      }

      const dreData = useDREStore.getState().dreData;
      if (!dreData) {
        toast.error('Nenhuma DRE carregada');
        set({ isSaving: false });
        return;
      }

      const versions = get().versions;
      const versionNumber = versions.length > 0 ? versions[0].versionNumber + 1 : 1;

      // Calcular diff em relação à versão anterior
      const previousVersion = versions[0];
      const diff = previousVersion 
        ? calculateDiff(previousVersion.dreData, dreData)
        : null;

      const { error } = await supabase
        .from('dre_versions')
        .insert({
          user_id: user.id,
          version_number: versionNumber,
          name,
          description: description || null,
          dre_data_snapshot: {
            setima_years: dreData.setima.years,
            studio555_years: dreData.studio555.years,
          },
          diff,
          created_by: user.email || 'Usuário',
          is_auto_checkpoint: isAutoCheckpoint, // 🎯 SPRINT 4: Flag checkpoint automático
        });

      if (error) throw error;

      // 🎯 SPRINT 4: Toast diferente para checkpoint vs manual save
      if (isAutoCheckpoint) {
        toast.success(`💾 Checkpoint automático "${name}" criado`);
      } else {
        toast.success(`Versão "${name}" salva com sucesso`);
      }
      
      // Recarregar versões para obter o ID da versão recém-criada
      await get().loadVersions();
      
      // ✅ NOVO: Marcar a versão recém-salva como atual E limpar versão base
      // Ela será a primeira da lista após recarregar
      const newVersions = get().versions;
      if (newVersions.length > 0) {
        set({ currentVersionId: newVersions[0].id, baseVersionId: null });
      }
    } catch (error) {
      console.error('Erro ao salvar versão:', error);
      toast.error('Erro ao salvar versão');
    } finally {
      set({ isSaving: false });
    }
  },

  // ==========================================================================
  // RESTAURAÇÃO
  // ==========================================================================

  restoreVersion: async (versionId) => {
    try {
      const version = get().versions.find((v) => v.id === versionId);
      if (!version) {
        toast.error('Versão não encontrada');
        return;
      }

      // Confirmar restauração
      const confirmed = window.confirm(
        `Deseja restaurar a versão "${version.name}"?\n\n` +
        `Isso carregará os dados desta versão. Se quiser salvar como nova versão, use o botão "Salvar Versão" depois.`
      );

      if (!confirmed) return;

      // ✅ MUDANÇA: Apenas restaurar DRE do snapshot (não criar nova versão)
      useDREStore.setState({ dreData: version.dreData });
      
      // ✅ NOVO: Marcar esta versão como atual E como base
      set({ currentVersionId: versionId, baseVersionId: versionId });
      
      // Salvar no banco (atualiza dre_data atual)
      await useDREStore.getState().saveDRE();

      toast.success(
        `Versão "${version.name}" restaurada!\n` +
        `Se fizer alterações e quiser salvar, clique em "Salvar Versão".`,
        { duration: 5000 }
      );
      
      // ❌ REMOVIDO: Não criar nova versão "Restauração: X"
      // O usuário pode criar manualmente se quiser
    } catch (error) {
      console.error('Erro ao restaurar versão:', error);
      toast.error('Erro ao restaurar versão');
    }
  },

  // ==========================================================================
  // EXCLUSÃO
  // ==========================================================================

  deleteVersion: async (versionId) => {
    try {
      const version = get().versions.find((v) => v.id === versionId);
      if (!version) {
        toast.error('Versão não encontrada');
        return;
      }

      // Confirmar exclusão
      const confirmed = window.confirm(
        `Deseja excluir a versão "${version.name}"?\n\n` +
        `Esta ação não pode ser desfeita.`
      );

      if (!confirmed) return;

      const { error } = await supabase
        .from('dre_versions')
        .delete()
        .eq('id', versionId);

      if (error) throw error;

      toast.success(`Versão "${version.name}" excluída`);
      
      // Recarregar versões
      await get().loadVersions();
    } catch (error) {
      console.error('Erro ao excluir versão:', error);
      toast.error('Erro ao excluir versão');
    }
  },

  // ==========================================================================
  // COMPARAÇÃO
  // ==========================================================================

  compareVersions: (v1Id, v2Id) => {
    const v1 = get().versions.find((v) => v.id === v1Id);
    const v2 = get().versions.find((v) => v.id === v2Id);

    if (!v1 || !v2) {
      toast.error('Versões não encontradas');
      return [];
    }

    return calculateDiff(v1.dreData, v2.dreData);
  },

  // ==========================================================================
  // GERENCIAMENTO DE VERSÃO BASE
  // ==========================================================================

  clearBaseVersion: () => {
    set({ baseVersionId: null });
  },

  // ==========================================================================
  // 🎯 SPRINT 3: ASSOCIAR PREMISSAS À VERSÃO DRE
  // ==========================================================================

  /**
   * Associa snapshot de premissas à versão DRE especificada
   * Chamado automaticamente ao clicar "Salvar e Recalcular" em PremissasPage
   * 
   * AGORA PERSISTE NO SUPABASE!
   * 
   * @param versionId - ID da versão DRE a atualizar (geralmente baseVersionId)
   * @param snapshot - Snapshot completo das premissas usadas no cálculo
   */
  updateVersionWithPremissas: async (versionId, snapshot) => {
    try {
      // 1. Atualizar no banco de dados
      const { error } = await supabase
        .from('dre_versions')
        .update({ premissas_snapshot: snapshot })
        .eq('id', versionId);

      if (error) {
        console.error('[versionStore] Erro ao persistir premissas no Supabase:', error);
        toast.error('Erro ao salvar premissas no histórico');
        return;
      }

      // 2. Atualizar no estado local
      set((state) => {
        const versionIndex = state.versions.findIndex((v) => v.id === versionId);
        
        if (versionIndex === -1) {
          console.warn(`[versionStore] Versão ${versionId} não encontrada para atualizar premissas`);
          return state;
        }

        const updatedVersions = [...state.versions];
        updatedVersions[versionIndex] = {
          ...updatedVersions[versionIndex],
          premissasSnapshot: snapshot,
        };

        console.log(`[versionStore] ✅ Premissas associadas e persistidas à versão "${updatedVersions[versionIndex].name}"`, {
          versionId,
          timestamp: new Date(snapshot.timestamp).toLocaleString('pt-BR'),
          beta_setima: snapshot.setima.beta,
          wacc_setima: (snapshot.setima.wacc * 100).toFixed(2) + '%',
          beta_555: snapshot.studios555.beta,
          wacc_555: (snapshot.studios555.wacc * 100).toFixed(2) + '%',
        });

        return { versions: updatedVersions };
      });

      // 3. Toast de sucesso
      toast.success('✅ Premissas associadas ao histórico', {
        duration: 3000,
        icon: '',
      });
    } catch (error) {
      console.error('[versionStore] Erro ao atualizar premissas:', error);
      toast.error('Erro ao associar premissas');
    }
  },
}));
